/**
 * /downhole/invoices — tool rental invoices (fieldiq-api#882
 * `GET /downhole/invoices`).
 *
 * ONE WRITE: "New invoice from runs" (api#910, admin and office_staff) — a
 * draft generated from billable runs (InvoiceDialogs.tsx). Send, PDF, void and
 * rebill are on the invoice itself. The empty state does NOT offer it: a write
 * is not the answer to a read, and an empty list may be a fresh tenant whose
 * runs have no rates yet.
 *
 * MONEY IS PER INVOICE. Each invoice carries its own currency (CAD or USD), so
 * every figure on a row is formatted in THAT currency, never the tenant's and
 * never a bare '$'. Totals are the stored ones.
 *
 * The date filter is the CREATED day in the tenant zone — what the API filters
 * on — and the filter says so, because it is not the due date.
 *
 * Chip and band counts are /dashboard's `invoices_by_status`; the outstanding
 * figure is its `outstanding_invoice_total`, which is NULL when the
 * outstanding invoices are in more than one currency.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, CircleDollarSign, FileText, Loader2, Plus, Send, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { canWriteDownholeInvoices } from '../../lib/downhole-roles';
import { GenerateInvoiceDialog } from './InvoiceDialogs';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeDashboard, useDownholeInvoices } from '../../hooks/useDownhole';
import { formatInstantDay, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { DH_INVOICE_STATUS_SKIN, dhSkin } from '../../lib/downhole-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_INVOICE_STATUSES } from '../../types/downhole';
import { DH_DATE_INPUT, DH_SECONDARY, DhBadge, DhEmpty, DhField, DhPanel } from './DownholeBits';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const isStatus = (v: string | null): v is (typeof DH_INVOICE_STATUSES)[number] =>
  !!v && (DH_INVOICE_STATUSES as readonly string[]).includes(v);

export default function InvoicesScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteDownholeInvoices(user?.role);
  const [generating, setGenerating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params = useMemo(
    () => ({ ...(status && { status }), ...(DAY.test(from) && { from }), ...(DAY.test(to) && { to }) }),
    [status, from, to],
  );
  const query = useDownholeInvoices(params);
  const dashboardQuery = useDownholeDashboard();
  const failure = queryLoadFailure(query);
  const invoices = query.data?.data ?? [];
  const tz = query.data?.meta.time_zone ?? null;
  const dashboard = dashboardQuery.data?.data ?? null;
  const byStatus = dashboard?.invoices_by_status;
  const countsLoading = dashboardQuery.isLoading;
  const filtered = Object.keys(params).length > 0;
  const total = byStatus ? DH_INVOICE_STATUSES.reduce((n, s) => n + (byStatus[s] ?? 0), 0) : undefined;

  const setStatus = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('status', next);
    else p.delete('status');
    setSearchParams(p, { replace: true });
  };
  const open = (id: string) => navigate(`/downhole/invoices/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  const chips = [
    { key: '', label: 'All', count: total },
    ...DH_INVOICE_STATUSES.map((s) => ({ key: s, label: dhSkin(DH_INVOICE_STATUS_SKIN, s).label, count: byStatus?.[s] })),
  ];

  return (
    <div data-testid="downhole-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Tool Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            {/* Finance → Invoices is the one list across verticals; this screen
                keeps the run-to-invoice flow and the downhole counts. */}
            <Link to="/finance/invoices?vertical=downhole_tools" className="fi-action fi-action--standard fi-action--secondary" data-testid="downhole-all-invoices-link">
              All invoices
            </Link>
            <Link to="/downhole/invoices/ready" className="fi-action fi-action--standard fi-action--secondary" data-testid="downhole-ready-link">
              Ready to invoice
            </Link>
            {canWrite && (
              <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setGenerating(true)}>
                <Plus className="size-4" aria-hidden="true" />
                New invoice from runs
              </button>
            )}
          </>
        }
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Draft', sub: 'Not sent', tone: 'neutral', loading: countsLoading, value: displayValue(byStatus?.draft) },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'Sent', sub: 'With the customer', tone: 'info', loading: countsLoading, value: displayValue(byStatus?.sent) },
          { icon: <Wallet className="size-4" aria-hidden="true" />, label: 'Paid', sub: 'Settled', tone: 'success', loading: countsLoading, value: displayValue(byStatus?.paid) },
          {
            icon: <CircleDollarSign className="size-4" aria-hidden="true" />,
            label: 'Outstanding',
            // NULL across mixed currencies: a CAD + USD sum is not a figure.
            sub: dashboard && dashboard.outstanding_invoice_total == null ? 'Mixed currencies' : 'Not yet paid',
            tone: 'accent',
            loading: countsLoading,
            value: fmtCurrency(dashboard?.outstanding_invoice_total, dashboard?.outstanding_invoice_currency, 0),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {chips.map((chip) => {
              const active = status === chip.key;
              return (
                <button
                  key={chip.key || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatus(chip.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {chip.label}
                  {!countsLoading && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{displayValue(chip.count)}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <DhField label="Created from">
                <input type="date" className={DH_DATE_INPUT} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
              </DhField>
            </div>
            <div className="w-44">
              <DhField label="Created to">
                <input type="date" className={DH_DATE_INPUT} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
              </DhField>
            </div>
            {(from || to) && (
              <button type="button" className={DH_SECONDARY} onClick={() => { setFrom(''); setTo(''); }}>
                Clear dates
              </button>
            )}
          </div>

          <DhPanel
            eyebrow="Billing"
            title="Invoices"
            testId="downhole-invoice-register"
            footer={
              !query.isLoading && !failure && invoices.length > 0 && filtered && total !== undefined
                ? `Showing ${invoices.length} of ${total} invoices`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading invoices" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="tool invoices" />
            ) : invoices.length === 0 ? (
              <DhEmpty>{filtered ? 'No invoice matches these filters.' : 'There are no tool invoices.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1200px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="whitespace-nowrap px-4 py-3">Invoice #</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Assembly</th>
                        <th scope="col" className="px-4 py-3">Well / Rig</th>
                        <th scope="col" className="px-4 py-3">Created</th>
                        <th scope="col" className="px-4 py-3">Due</th>
                        <th scope="col" className="px-4 py-3 text-right">Total</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr
                          key={inv.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(inv.id)}
                          onKeyDown={rowKeys(inv.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            dhSkin(DH_INVOICE_STATUS_SKIN, inv.status).rail
                          }`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{inv.invoice_number || '—'}</td>
                          <td className="px-4 py-3"><DhBadge map={DH_INVOICE_STATUS_SKIN} value={inv.status} /></td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(inv.customer_name)}</td>
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap font-mono text-sm text-white">{inv.assembly_number || '—'}</p>
                            {inv.run_number != null && <p className="text-xs text-fieldiq-muted">Run {inv.run_number}</p>}
                          </td>
                          <td className="min-w-[14rem] px-4 py-3 text-sm text-fieldiq-text">
                            {inv.well_name || inv.rig_name ? `${inv.well_name || '—'} / ${inv.rig_name || '—'}` : '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(inv.created_at, tz) ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantTenantDay(inv.due_date, tz) ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm font-extrabold text-white">{fmtCurrency(inv.total, inv.currency)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {invoices.map((inv) => (
                    <div
                      key={inv.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(inv.id)}
                      onKeyDown={rowKeys(inv.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{inv.invoice_number || '—'}</p>
                        <DhBadge map={DH_INVOICE_STATUS_SKIN} value={inv.status} />
                      </div>
                      <p className="mt-1 text-sm text-white">{displayValue(inv.customer_name)}</p>
                      <div className="mt-2 flex items-center justify-between text-xs text-fieldiq-muted">
                        <span>Due {formatInstantTenantDay(inv.due_date, tz) ?? '—'}</span>
                        <span className="font-mono text-sm font-extrabold text-white">{fmtCurrency(inv.total, inv.currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
      {canWrite && generating && <GenerateInvoiceDialog open onClose={() => setGenerating(false)} tz={tz} />}
    </div>
  );
}
